import { AddTransaction } from "@/components/history/AddTransaction";
import { InvoicesTableMeta } from "@/components/history/InvoiceHistory";
import { ConfirmDelete } from "@/shared/confirmDelete/ConfirmDelete";
import { $blockRowNavigate, $transactions } from "@/stores";
import { InvoicesHistoryData } from "@/types";
import { useClickOutside } from "@/utils";
import { Row, Table } from "@tanstack/react-table"
import { ArchiveRestore, BookCopy, FilePlus, MoreHorizontal, Trash2 } from "lucide-react"
import { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CSSTransition } from "react-transition-group";

interface DataTableRowActionsProps<TData> {
    row: Row<InvoicesHistoryData>
    table: Table<TData>
}

export function InvoiceTableRowActions<TData>({
    row,
    table
}: DataTableRowActionsProps<TData>) {
    const { openOptionsId,
        setOpenOptionsId,
        addTransaction: addTransactionFn,
        archiveInvoice,
        deleteInvoice } = table.options.meta as InvoicesTableMeta;
    const [addTransaction, setIsAddTransaction] = useState(false);
    const [archive, setIsArchive] = useState(false);
    const [deleteInvoiceOpen, setDeleteInvoiceOpen] = useState(false);
    const [searchParams] = useSearchParams();
    const areaRef = useRef(null);

    const transactionCount = $transactions.value.values.filter((item) => item.invoiceNumber === row.original.invoiceNumber).length;

    useClickOutside(areaRef, () => {
        if (row.id === openOptionsId) setOpenOptionsId(null);
        if (openOptionsId) {
            setTimeout(() => {
                $blockRowNavigate.set(false);
            }, 200);
        }
    }, "row-actions__trigger-button");

    return (
        <div className="row-actions" onClick={(e) => e.stopPropagation()}>
            <div className="row-actions__container">
                <button className="row-actions__trigger-button" onClick={() => {
                    $blockRowNavigate.set(true);
                    setOpenOptionsId(row.id);
                }}>
                    <MoreHorizontal width={18} height={18} />
                </button>
                <CSSTransition in={row.id === openOptionsId}
                    timeout={200}
                    classNames="fade"
                    exit={false}
                    unmountOnExit>
                    <div className="row-actions__options" ref={areaRef}>
                        <ul className="row-actions__options-list">
                            <li className="row-actions__option">
                                <button className="row-actions__option-button" onClick={() => {
                                    setIsAddTransaction(true);
                                    setOpenOptionsId(null);
                                }}>
                                    <FilePlus width={16} height={16} />
                                    <p>Add transaction</p>
                                </button>
                            </li>
                            <li className="row-actions__option">
                                <Link className="row-actions__option-button" to={{
                                    pathname: "/",
                                    search: `template=${row.original.invoiceNumber}`
                                }}
                                    state={{ prevPath: `/history?${searchParams.toString()}` }}>
                                    <BookCopy width={16} height={16} />
                                    Duplicate
                                </Link>
                            </li>

                            <li className="row-actions__option row-actions__warning">
                                <button className="row-actions__option-button row-actions__warning" onClick={() => {
                                    setIsArchive(true);
                                    setOpenOptionsId(null);
                                }} >
                                    <ArchiveRestore width={16} height={16} />
                                    <p>Archive</p>
                                </button>
                            </li>
                            {transactionCount === 0 && <li className="row-actions__option row-actions__warning">
                                <button className="row-actions__option-button row-actions__warning" onClick={() => {
                                    setDeleteInvoiceOpen(true);
                                    setOpenOptionsId(null);
                                }} >
                                    <Trash2 width={16} height={16} />
                                    <p>Delete</p>
                                </button>
                            </li>}
                        </ul>
                    </div>
                </CSSTransition>
            </div>
            {addTransaction && <AddTransaction action={addTransactionFn} invoice={row.original} close={() => {
                setIsAddTransaction(false);
            }} />}
            {archive && <div className="overlay">
                <ConfirmDelete
                    type="Invoice"
                    action={"Archive"}
                    id={row.original.invoiceNumber}
                    onClose={() => { setIsArchive(false) }}
                    onDelete={async () => {
                        await archiveInvoice(row.original.invoiceNumber);
                    }}
                />
            </div>}
            {deleteInvoiceOpen && <div className="overlay">
                <ConfirmDelete
                    type="Invoice"
                    action={"Delete"}
                    id={row.original.invoiceNumber}
                    onClose={() => { setDeleteInvoiceOpen(false) }}
                    onDelete={async () => {
                        await deleteInvoice(row.original.invoiceNumber);
                    }}
                />
            </div>}
        </div>
    )
}